import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, CalendarCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genId = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : 'sess-' + Math.random().toString(36).slice(2) + Date.now();

const GREETING = {
  role: 'assistant',
  content: "Hi! I'm the Go-To Media assistant. Ask me anything about our video packages — or book a free discovery call and our team will make you the local celebrity. How can I help?",
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', phone: '', topic: '' });
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  // Auto-open the chat once per session, a few seconds after load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem('gtmc_chat_seen')) {
        setOpen(true);
        sessionStorage.setItem('gtmc_chat_seen', '1');
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  const toggleOpen = () => {
    sessionStorage.setItem('gtmc_chat_seen', '1');
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, showForm]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Sorry, I'm having trouble right now. Please try again or email gotomediacollective@gmail.com." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/chat/discovery`, { session_id: sessionId, ...lead });
      toast.success('Discovery Call Requested!', { description: res.data.message });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Thanks ${lead.name.split(' ')[0] || ''}! Your discovery call request is in — our team will reach out to ${lead.email} shortly.` },
      ]);
      setShowForm(false);
      setLead({ name: '', email: '', phone: '', topic: '' });
    } catch (err) {
      toast.error('Something went wrong', { description: 'Please try again or email gotomediacollective@gmail.com' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={toggleOpen}
        aria-label={open ? 'Close chat' : 'Open chat'}
        data-testid="chat-toggle-button"
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform duration-300 hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #f4d66f 0%, #d4af37 55%, #b8941f 100%)' }}
      >
        {open ? <X size={26} className="text-black" /> : <MessageCircle size={26} className="text-black" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-8rem)] flex flex-col rounded-2xl overflow-hidden border border-[#d4af37]/30 shadow-2xl bg-[#0f0f0f]"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-[#050505]">
            {showForm && (
              <button onClick={() => setShowForm(false)} data-testid="chat-back-button" className="text-[#a0a0a0] hover:text-white" aria-label="Back to chat">
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="w-9 h-9 rounded-full bg-[#d4af37]/15 flex items-center justify-center">
              <MessageCircle size={18} className="text-[#d4af37]" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm leading-tight">Go-To Media Assistant</p>
              <p className="text-[#a0a0a0] text-xs">Typically replies instantly</p>
            </div>
          </div>

          {!showForm ? (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-[#d4af37] text-black rounded-br-sm'
                          : 'bg-[#1a1a1a] text-[#e5e5e5] rounded-bl-sm border border-white/5'
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-[#1a1a1a] border border-white/5 text-[#a0a0a0] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm">
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-bounce"></span>
                        <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                        <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Discovery CTA */}
              <button
                onClick={() => setShowForm(true)}
                data-testid="chat-request-call-button"
                className="mx-4 mb-2 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#d4af37]/40 text-[#d4af37] text-sm font-medium hover:bg-[#d4af37]/10 transition-colors"
              >
                <CalendarCheck size={16} />
                Request a Discovery Call
              </button>

              {/* Input */}
              <form onSubmit={sendMessage} className="flex items-center gap-2 p-4 border-t border-white/10 bg-[#050505]">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  data-testid="chat-input"
                  className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-full px-4 py-2.5 text-white text-sm placeholder-[#6b6b6b] focus:outline-none focus:border-[#d4af37]"
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  data-testid="chat-send-button"
                  aria-label="Send message"
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-opacity"
                  style={{ background: 'linear-gradient(135deg, #f4d66f 0%, #d4af37 100%)' }}
                >
                  <Send size={18} className="text-black" />
                </button>
              </form>
            </>
          ) : (
            /* Discovery form */
            <form onSubmit={submitLead} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="discovery-form">
              <p className="text-[#a0a0a0] text-sm">Share your details and we&apos;ll reach out to schedule your free discovery call.</p>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-2">Your Name *</label>
                <input
                  type="text" required value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  data-testid="discovery-name-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-2">Email *</label>
                <input
                  type="email" required value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  data-testid="discovery-email-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-2">Phone</label>
                <input
                  type="tel" value={lead.phone}
                  onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                  data-testid="discovery-phone-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#a0a0a0] mb-2">What would you like to discuss? *</label>
                <textarea
                  required rows="3" value={lead.topic}
                  onChange={(e) => setLead({ ...lead, topic: e.target.value })}
                  data-testid="discovery-topic-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-[#d4af37]"
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="discovery-submit-button"
                className="cta-button w-full disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Request Discovery Call'}
                <CalendarCheck size={18} />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
